import {useDispatch} from 'react-redux';

import {validateColumnValue} from 'src/modules/bulk-update/add-attendees/services/utils';

import {TDataType as DataType} from '@pb/kit-component-table-interactions';
import {pluralize} from '@pb/kit-formatters';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';


import {updateColumnByRowIndexes} from '../../services/add-attendees-bulk.slice';

import {HEADERS} from './constants';

import type {
    OnChangeOptionOrder,
    OnCreateOption,
    OnDelete,
    OnDeleteOption,
    OnSave,
    OnSelectOption,
    OnSelectOptionCount,
    OnShownCaption,
    OnUpdateOption,
    OnValidateSelectOptionValue,
    OnValuesCountByOption,
} from '@pb/kit-component-table-interactions';
import type {
    InteractiveColumn,
    UpdatedData,
} from 'src/components/with-interaction-table/typedef';

type Props = {
    getCurrentColumn: (columnIndex: number) => InteractiveColumn | null,
};

export const useValidateColumnsApi = ({getCurrentColumn}: Props) => {
    const dispatch = useDispatch();

    const onSave = useCallbackWithRef<OnSave>(({columnIndex, requestData}) => {
        const currentColumn = getCurrentColumn(columnIndex);
        const updatedData: UpdatedData[] = [];
        const invalidColumnIds: string[] = [];

        if (currentColumn) {
            requestData.forEach(({rowIndex, value}) => {
                switch (currentColumn.dataType) {
                    case DataType.TEXT:
                        const formattedValue = value?.toString().trim();
                        const validationError = validateColumnValue(currentColumn.columnId, formattedValue);
                        if (validationError) {
                            invalidColumnIds.push(currentColumn.columnId);
                        }
                        updatedData.push({
                            columnId: currentColumn.columnId,
                            value: formattedValue,
                            rowId: rowIndex,
                            validationError: validationError?.message || null,
                        });
                }
            });

            dispatch(updateColumnByRowIndexes(updatedData));
        }

        const invalidColumns = [...new Set(invalidColumnIds)].map(columnId => HEADERS[columnId]);
        const error = invalidColumns.length
            ? pluralize(`${invalidColumns.join(',')} [is, are] not valid`, invalidColumns.length)
            : null;
        return new Promise((resolve, reject) => {
            if (error) {
                reject({
                    error: true,
                    data: {
                        reason: 'INVALID_COLUMNS',
                        message: error,
                    },
                });
            } else {
                resolve({
                    success: true,
                    data: requestData.map(({rowIndex, value}) => ({
                        entityId: rowIndex,
                        data: value,
                    })),
                });
            }
        });
    });

    const onDelete = useCallbackWithRef<OnDelete>(({columnIndex, requestData}) => {
        const currentColumn = getCurrentColumn(columnIndex);
        const updatedData: UpdatedData[] = [];
        const invalidColumnIds: string[] = [];

        if (currentColumn) {
            requestData.forEach(({rowIndex}) => {
                switch (currentColumn.dataType) {
                    case DataType.TEXT:
                        const validationError = validateColumnValue(currentColumn.columnId, '');
                        if (validationError) {
                            invalidColumnIds.push(currentColumn.columnId);
                        }
                        updatedData.push({
                            columnId: currentColumn.columnId,
                            value: '',
                            rowId: rowIndex,
                            validationError: validationError?.message || null,
                        });
                }
            });

            dispatch(updateColumnByRowIndexes(updatedData));
        }

        const invalidColumns = [...new Set(invalidColumnIds)].map(columnId => HEADERS[columnId]);
        const error = invalidColumns.length
            ? pluralize(`${invalidColumns.join(',')} [is, are] required field`, invalidColumns.length)
            : null;
        return new Promise((resolve, reject) => {
            if (error) {
                reject({
                    error: true,
                    data: {
                        reason: 'INVALID_COLUMNS',
                        message: error,
                    },
                });
            } else {
                resolve({
                    success: true,
                    data: requestData.map(({rowIndex, value}) => ({
                        entityId: rowIndex,
                        data: value,
                    })),
                });
            }
        });
    });

    const onCreateOption = useCallbackWithRef<OnCreateOption>(() => {
        return new Promise(() => undefined);
    });

    const onDeleteOption = useCallbackWithRef<OnDeleteOption>(() => {
        return new Promise(() => undefined);
    });

    const onSelectOption = useCallbackWithRef<OnSelectOption>(() => {
        return new Promise(() => undefined);
    });

    const onUpdateOption = useCallbackWithRef<OnUpdateOption>(() => {
        return new Promise(() => undefined);
    });

    const onChangeOptionOrder = useCallbackWithRef<OnChangeOptionOrder>(() => {
        return new Promise(() => undefined);
    });

    const onValidateSelectOptionValue = useCallbackWithRef<OnValidateSelectOptionValue>(() => {
        return new Promise(() => undefined);
    });

    const onSelectOptionCount = useCallbackWithRef<OnSelectOptionCount>(() => {
        return new Promise(() => undefined);
    });

    const onValuesCountByOption = useCallbackWithRef<OnValuesCountByOption>(() => {
        return new Promise(() => undefined);
    });

    const onShownCaption = useCallbackWithRef<OnShownCaption>(() => {
        return new Promise(() => undefined);
    });

    return {
        onSave,
        onDelete,
        onCreateOption,
        onSelectOption,
        onDeleteOption,
        onUpdateOption,
        onChangeOptionOrder,
        onSelectOptionCount,
        onValuesCountByOption,
        onValidateSelectOptionValue,
        onShownCaption,
    };
};
